import { Button, Card, DatePicker, Form, Input, InputNumber, notification, Select, Row, Col, Divider, Spin, Space } from "antd";
import React, { useEffect, useMemo, useCallback, useState } from "react";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useAuth } from "services/Auth";
import PageLayoutWrapper from "components/PageLayoutWrapper";
import { offlineHandler } from "utils/offlineCache";
import useSelectedService from "hooks/useSelectedService";
import { locationTracker } from "utils/location/geolocation";
import { generateActionReference } from "utils/location/record";
import { LOCATION_ACTIONS } from "constants/location";
import dayjs from "dayjs";
const { TextArea } = Input;
const isMissingTokenQueueError = (response) =>
  response?.status === false &&
  String(response?.data || "")
    .toLowerCase()
    .includes("staff token is required");

const InlineQuantity = ({ value = 0, onChange }) => (
  <Space.Compact style={{ display: "flex" }}>
    <Button onClick={() => onChange(Math.max(1, value - 1))}>−</Button>
    <InputNumber className="input-text-center" value={value} min={1} controls={false} style={{ width: 60 }} onChange={(v) => onChange(v ?? 1)} />
    <Button onClick={() => onChange(value + 1)}>+</Button>
  </Space.Compact>
);

const Transfers = () => {
  const { auth } = useAuth();
  const apiClient = useMemo(() => offlineHandler(auth.data), [auth.data]);
  const { selectedService } = useSelectedService();

  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  const [wares, setWares] = useState([]);
  const [equipments, setEquipments] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const waresResponse = await apiClient.getMany("/goods/wares");
      if (!waresResponse.status) {
        notification.error({
          title: "Erro",
          description: "Erro ao carregar dados.",
        });
        await apiClient.post("/error-capture", {
          error: waresResponse.data,
          action: "/Transfers/loadData/ getMany /goods/wares",
          timestamp: new Date().toISOString(),
        });
        return;
      }

      const equipmentsResponse = await apiClient.getMany("/equipments");
      if (!equipmentsResponse.status) {
        notification.error({
          title: "Erro",
          description: "Erro ao carregar dados.",
        });
        await apiClient.post("/error-capture", {
          error: equipmentsResponse.data,
          action: "/Transfers/loadData/ getMany /equipments",
          timestamp: new Date().toISOString(),
        });
        return;
      }
      const waresData = waresResponse.data.filter((w) => w.type === "goods");
      const equipmentsData = equipmentsResponse.data.filter((e) => e.warehouse === true);
      setWares(waresData);
      setEquipments(equipmentsData);
      form.setFieldValue("date", dayjs());

      // Error handling
    } catch (error) {
      notification.error({
        title: "Erro",
        description: "Erro ao carregar dados.",
      });
      await apiClient.post("/error-capture", {
        error: error,
        action: "/Transfers/loadData/catch",
        timestamp: new Date().toISOString(),
      });
    }
  }, [apiClient, form]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (values) => {
    try {
      setIsLoading(true);

      const { date, equipment_1, equipment_2, transfers, notes } = values;

      if (!transfers?.length) {
        notification.warning({
          title: "Aviso",
          description: "Adicione pelo menos um artigo.",
        });
        return;
      }

      for (const item of transfers) {
        const payload = {
          type: "67e15380419c69b84d23d9aa", //transfer type id
          date: dayjs(date).toDate(),
          equipment_1,
          equipment_2,
          wares: item.ware,
          quantity: item.quantity,
          notes,
        };

        const response = await apiClient.post("/goods/transfer", payload);

        if (response?.status === false) {
          if (isMissingTokenQueueError(response)) {
            notification.error({
              title: "Ação bloqueada",
              description: "Não foi possível guardar a transferência offline porque falta o token do staff. Faz login online e tenta novamente.",
            });
            return;
          }
          notification.error({
            title: "Erro",
            description: `Erro ao criar transferência (${item.ware})`,
          });
          await apiClient.post("/error-capture", {
            error: response.data,
            action: "/Transfers/handleSubmit/ post /goods/transfer",
            timestamp: new Date().toISOString(),
          });
        }
      }

      // Capture the beverage-transfer location (best-effort; never blocks the action).
      // No-op when there is no active service (nothing to attribute the position to).
      locationTracker.captureActionLocation({
        service_id_raw: selectedService?.service_id_raw || null,
        equipment_id: selectedService?.equipment?.[0]?._id || equipment_2 || null,
        action: LOCATION_ACTIONS.BEVERAGE_TRANSFER,
        action_reference: generateActionReference(),
      });

      notification.success({
        title: "Sucesso",
        description: "Transferências criadas com sucesso.",
      });

      // Send info capture to the server for debugging purposes
      await apiClient.post("/info-capture", {
        extraData: {
          location: "src/views/app-views/goods/transfers/index.js/handleSubmit",
          timestamp: new Date().toISOString(),
        },
        info: {
          location: "src/views/app-views/goods/transfers/index.js/handleSubmit",
          timestamp: new Date().toISOString(),
          equipment_1: values.equipment_1 || null,
          equipment_2: values.equipment_2 || null,
          payload: { date: values.date, transfers: values.transfers, notes: values.notes },
        },
        subject: "Boats: Mobile Transfers",
        message: `Boats: Beverage transfers created (${auth?.data?.name || "Unknown User"})`,
      });

      form.resetFields();
      form.setFieldValue("date", dayjs());
    } catch (error) {
      console.error("Error submitting transfers:", error);
      notification.error({
        title: "Erro",
        description: "Erro ao guardar transferências.",
      });
      await apiClient.post("/error-capture", {
        error: error,
        action: "/Transfers/handleSubmit/catch",
        timestamp: new Date().toISOString(),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageLayoutWrapper loading={isLoading} title="Transferências de Bebidas">
      <Spin spinning={isLoading} fullscreen />
      <Card size="small">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="compact-form"
          initialValues={{
            transfers: [{ ware: null, quantity: 1 }],
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              {/*retirado disabled a pedido da SC a 01/04/2026 */}
              <Form.Item label="Data / Hora" name="date" rules={[{ required: true, message: "Campo obrigatório" }]}>
                <DatePicker showTime format="DD-MM-YYYY HH:mm" style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Observações" name="notes">
                <TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Armazém de Origem" name="equipment_1" rules={[{ required: true, message: "Campo obrigatório" }]}>
                <Select
                  showSearch
                  allowClear
                  placeholder="Origem"
                  options={equipments?.map((e) => ({
                    value: e._id,
                    label: e.name,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Armazém de Destino" name="equipment_2" rules={[{ required: true, message: "Campo obrigatório" }]}>
                <Select
                  showSearch
                  allowClear
                  placeholder="Destino"
                  options={equipments?.map((e) => ({
                    value: e._id,
                    label: e.name,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Artigos</Divider>

          <Form.List name="transfers">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => {
                  return (
                    <Row key={key} gutter={12} align="top">
                      <Col xs={24} md={12}>
                        <Form.Item name={[name, "ware"]} rules={[{ required: true }]}>
                          <Select
                            showSearch
                            placeholder="Artigo"
                            options={wares.map((w) => ({
                              value: w._id,
                              label: w.description,
                            }))}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={20} md={10}>
                        <Form.Item name={[name, "quantity"]} rules={[{ required: true }]} initialValue={1}>
                          <InlineQuantity />
                        </Form.Item>
                      </Col>

                      <Col xs={2} md={2}>
                        <Button danger icon={<DeleteOutlined />} onClick={() => remove(name)} disabled={fields.length === 1} />
                      </Col>
                    </Row>
                  );
                })}

                <div style={{ textAlign: "center" }}>
                  <Button
                    className="shopping-button"
                    style={{
                      height: 30,
                      fontSize: 16,
                      fontWeight: 700,
                      borderRadius: 12,
                    }}
                    type="dashed"
                    onClick={() => add({ quantity: 1 })}
                    icon={<PlusOutlined />}
                  >
                    Adicionar Artigo
                  </Button>
                </div>
              </>
            )}
          </Form.List>

          <Divider />

          <Row justify="end">
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Criar Transferências
            </Button>
          </Row>
        </Form>
      </Card>
    </PageLayoutWrapper>
  );
};

export default Transfers;
